import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table.js'
import { fromSdkTable, TableAdapter, type ColumnAdapter } from '@makyoapp/makyo-common'

import { TableFormattedData } from '@/core/db'
import { type IRow } from '@/core/models/tables/row'
import { queryKeys } from '@/lib/react-query'
import { StorageUtil } from '@/lib/storage'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { STORAGE_KEYS } from '@/shared/constants'
import { getSafely } from '@/shared/safely'

// get persisted pagination state
const storage = new StorageUtil()

// get persisted table pagination
function getInitialPagination(tableId: string) {
  const initialPaginationData = (storage.getItem(STORAGE_KEYS.GRID_PAGINATION) || {}) as Record<
    string,
    { page: number; perPage: number }
  >

  if (initialPaginationData && tableId in initialPaginationData) {
    return initialPaginationData[tableId] ?? { page: 0, perPage: 100 }
  }
  return { page: 0, perPage: 100 }
}

//--------------------------------------------------------------------------------------//
//                                  Table Data Hook                                     //
//--------------------------------------------------------------------------------------//

const initialData: TableFormattedData = {
  rows: [] as IRow[],
  filteredRowsCount: 0,
  allRowsCount: 0,
}

/**
 * !! DO NOT TOUCH !! - this is the final implementation of useGridTableData!
 *
 * Custom hook for managing database tables operations
 *
 */
export const useGridTableData = (
  tableId: string,
  searchConfig: { type: 'column' | 'row'; columnTypes: ColumnTypes[]; value: string }
) => {
  // access query client
  const queryClient = useQueryClient()

  // access db client and worker
  const { dbClient, dbWorker, language, userSpecific, dbWorkerStatus, loadedTables } = useDbClientContext()
  const table = dbClient?.tablesMap.get(tableId)

  // initialize table state
  const [tableAdapter, setTableAdapter] = useState<TableAdapter | undefined>(
    table ? fromSdkTable(table.settings) : undefined
  )
  const [columns, setColumns] = useState<ColumnAdapter[]>(table ? table.GetColumns() : [])

  // local State
  const [pagination, setPagination] = useState(getInitialPagination(tableId))
  const [loading, setLoading] = useState(false)

  // update pagination when table changes
  useEffect(() => {
    setPagination(getInitialPagination(tableId))
  }, [tableId])

  const handlePaginationChange = useCallback(
    async (page: number, perPage: number) => {
      setPagination({ page, perPage })

      storage.setItem(STORAGE_KEYS.GRID_PAGINATION, {
        ...(storage.getItem(STORAGE_KEYS.GRID_PAGINATION) || {}),
        [tableId]: { page, perPage },
      })
    },
    [tableId]
  )

  //--------------------------------------------------------------------------------------//
  //                                  Fetch Handlers                                      //
  //--------------------------------------------------------------------------------------//

  // this act as both loading state and query dependency
  const tableStatus = dbWorkerStatus === 'READY' && !!tableId && loadedTables.has(tableId) ? 'READY' : 'PENDING'

  const memoizedParams = useMemo(() => {
    return [
      language,
      userSpecific,
      pagination,
      undefined,
      undefined,
      searchConfig.type === 'row' ? searchConfig.value : undefined,
      undefined,
    ] as const
  }, [language, pagination, searchConfig.type, searchConfig.value, userSpecific])

  // create a stable query key based on the current params
  const stableQueryKey = useMemo(() => {
    const [language, userSpecific, pagination, filter, sort, search, userFilter] = memoizedParams
    return [
      queryKeys.gridTableData,
      `t:${tableId}`,
      `ln:${language}`,
      `us:${userSpecific}`,
      `p:${pagination.page}:${pagination.perPage}`,
      `q:${search ?? ''}`,
      `ts:${tableStatus}`,
    ]
  }, [memoizedParams, tableId, tableStatus])

  /**
   * initialize query
   */
  const dataQuery = useQuery({
    queryKey: stableQueryKey,
    queryFn: async () => {
      if (!table) return
      return table.GetFormattedData(...memoizedParams) ?? initialData
    },
    enabled: !!table && tableStatus === 'READY',
    staleTime: Infinity, // keep data fresh to prevent additional fetch
  })

  // derived loading states
  const isLoadingData = dataQuery.isLoading
  const isLoading = tableStatus === 'PENDING' || isLoadingData || loading

  // derived states
  const { rows, filteredRowsCount, allRowsCount } = getSafely(dataQuery, ['data'], initialData)

  const totalPages = useMemo(() => Math.ceil(filteredRowsCount / pagination.perPage), [filteredRowsCount, pagination])

  //--------------------------------------------------------------------------------------//
  //                                  Effects                                             //
  //--------------------------------------------------------------------------------------//

  /**
   * request table data from worker
   * @dbWorker
   */
  useEffect(() => {
    if (!dbWorker || !table) return

    setLoading(true)
    setTableAdapter(fromSdkTable(table.settings))
    setColumns(table.GetColumns())

    const fetchData = () => {
      dbWorker.sendAction({
        type: 'get_table_data',
        payload: {
          table_id: table.settings._id,
          language,
        },
      })
    }
    fetchData()

    // refetch data when the tab or browser window is focused
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchData()
      }
    }

    // window.addEventListener('focus', handleVisibilityChange)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      // window.removeEventListener('focus', handleVisibilityChange)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [dbWorker, language, table])

  /**
   * subscribe to table data changes from db-worker
   */
  useEffect(() => {
    if (!table) return

    const unsubscribe = table.subscribe(() => {
      queryClient.invalidateQueries({ queryKey: stableQueryKey })
      setTableAdapter(fromSdkTable(table.settings))
      setColumns(table.GetColumns())
      setLoading(false)
    })

    return () => {
      unsubscribe()
    }
  }, [stableQueryKey, queryClient, table])

  /**
   * reset pagination when current page is greater than total pages
   */
  useEffect(() => {
    if (dataQuery.isFetched && pagination.page > 0 && pagination.page >= totalPages) {
      handlePaginationChange(0, pagination.perPage)
    }
  }, [totalPages, pagination, handlePaginationChange, dataQuery.isFetched])

  //
  return {
    dbWorker,
    tableAdapter,
    columns,
    setColumns,
    rows,
    filteredRowsCount,
    allRowsCount,
    isLoading,
    pagination,
    handlePaginationChange,
    totalPages,
  }
}
